import { Check, X, AlertCircle } from 'lucide-react';

export type ItemStatus = 'unchecked' | 'pass' | 'fail' | 'needs_attention';

interface ChecklistItemProps {
  id: string;
  text: string;
  status: ItemStatus;
  notes?: string;
  onStatusChange: (status: ItemStatus) => void;
  onNotesChange: (notes: string) => void;
}

export function ChecklistItem({
  id,
  text,
  status,
  notes,
  onStatusChange,
  onNotesChange,
}: ChecklistItemProps) {
  const getStatusStyles = () => {
    switch (status) {
      case 'pass':
        return 'bg-green-50 border-green-300';
      case 'fail':
        return 'bg-red-50 border-red-300';
      case 'needs_attention':
        return 'bg-yellow-50 border-yellow-300';
      default:
        return 'bg-white border-gray-200';
    }
  };

  return (
    <div className={`border rounded-lg p-3 transition-all ${getStatusStyles()}`}>
      <div className="flex items-start gap-3">
        <div className="flex gap-2 flex-shrink-0 mt-1">
          <button
            type="button"
            onClick={() => onStatusChange(status === 'pass' ? 'unchecked' : 'pass')}
            className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${
              status === 'pass'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-400 hover:bg-green-100 hover:text-green-600'
            }`}
            title="Pass"
          >
            <Check size={18} />
          </button>
          <button
            type="button"
            onClick={() => onStatusChange(status === 'fail' ? 'unchecked' : 'fail')}
            className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${
              status === 'fail'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-400 hover:bg-red-100 hover:text-red-600'
            }`}
            title="Fail"
          >
            <X size={18} />
          </button>
          <button
            type="button"
            onClick={() => onStatusChange(status === 'needs_attention' ? 'unchecked' : 'needs_attention')}
            className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${
              status === 'needs_attention'
                ? 'bg-yellow-600 text-white'
                : 'bg-gray-100 text-gray-400 hover:bg-yellow-100 hover:text-yellow-600'
            }`}
            title="Needs Attention"
          >
            <AlertCircle size={18} />
          </button>
        </div>
        <div className="flex-1 min-w-0">
          <label htmlFor={id} className="text-sm text-gray-800 cursor-pointer">
            {text}
          </label>
          {(status === 'fail' || status === 'needs_attention') && (
            <textarea
              id={`${id}-notes`}
              value={notes || ''}
              onChange={(e) => onNotesChange(e.target.value)}
              placeholder="Add notes about this issue..."
              className="w-full mt-2 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={2}
            />
          )}
        </div>
      </div>
    </div>
  );
}
